import { useEffect, useRef } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { MapPin } from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * Address autocomplete input powered by Google Places.
 * Falls back to a plain text input if the API key is missing.
 *
 * Props:
 * - value, onChange (string, standard controlled input)
 * - onPlaceSelected({ address, lat, lng, place_id }) — fires when the user
 *   picks a suggestion from the dropdown.
 * - placeholder, testId, country (default "GD" for Grenada)
 */
export default function AddressAutocomplete(props) {
  if (!GOOGLE_MAPS_API_KEY) {
    return <FallbackInput {...props} />;
  }
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={["places"]}>
      <AutocompleteInner {...props} />
    </APIProvider>
  );
}

function AutocompleteInner({
  value,
  onChange,
  onPlaceSelected,
  placeholder = "Delivery address",
  testId = "address-autocomplete-input",
  country = "GD",
  inputClassName = "",
}) {
  const inputRef = useRef(null);
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || !inputRef.current) return;
    const autocomplete = new places.Autocomplete(inputRef.current, {
      fields: ["formatted_address", "geometry", "place_id", "name"],
      componentRestrictions: country ? { country } : undefined,
    });
    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place || !place.geometry?.location) return;
      const address = place.formatted_address || place.name || "";
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      onChange?.(address);
      onPlaceSelected?.({ address, lat, lng, place_id: place.place_id });
    });
    return () => {
      window.google?.maps?.event?.removeListener(listener);
      // Remove the pac-container attached by Places when the input is unmounted
      document.querySelectorAll(".pac-container").forEach((el) => el.remove());
    };
  }, [places, country, onChange, onPlaceSelected]);

  return (
    <div className="relative">
      <MapPin className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none z-10" />
      <input
        ref={inputRef}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className={
          inputClassName ||
          "w-full pl-11 pr-4 py-3 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none"
        }
      />
    </div>
  );
}

function FallbackInput({
  value,
  onChange,
  placeholder = "Delivery address",
  testId = "address-autocomplete-input",
  inputClassName = "",
}) {
  return (
    <div className="relative">
      <MapPin className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className={
          inputClassName ||
          "w-full pl-11 pr-4 py-3 rounded-2xl bg-eatup-gray border border-transparent focus:border-eatup-green focus:bg-white focus:ring-4 focus:ring-eatup-green/10 outline-none"
        }
      />
    </div>
  );
}
